import { AUTH_URL } from './config.js';
import { BN_AUTH_CHANGE } from './events.js';
import type { Profile } from './profile.js';

// 'unknown' is the cold-start value — before the first fetchMe() has
// resolved (or 401'd) there is no answer yet, and that has to read
// differently from a *confirmed* 'signed-out', or a game finishing before
// the menu's warm-up fetchMe() lands would write a signed-in player's match
// to local history (see the M7c history-routing policy this state feeds).
export type AuthStatus = 'unknown' | 'signed-in' | 'signed-out';

export interface AuthSnapshot {
  readonly authState: AuthStatus;
  readonly userId: string | null;
}

const TTL_MS = 5 * 60 * 1000;
class ProfileCache {
  private data: Profile | null = null;
  private expiresAt: number = 0;
  // The sticky answer to "is there a session", tracked separately from
  // `data`'s freshness. The two are different lifetimes — the session cookie
  // lasts 30 days, `data` is re-fetched every 5 minutes — and conflating them
  // meant isLoggedIn() started reporting false for a still-signed-in player
  // once the TTL lapsed. That gates the history-sync write in every game's
  // serverSync.ts, so an online match finishing more than 5 minutes after the
  // last fetch was silently never recorded. Only moves on a resolved
  // fetchMe(), an explicit 401, or logout() — never on a network error or a
  // clock.
  private status: AuthStatus = 'unknown';
  // The account the sticky status last spoke for, so a caller that needs to
  // attribute something (the M7c history outbox) doesn't have to keep its
  // own copy in sync with this cache.
  private userId: string | null = null;
  // Bumped on every clear(). `requestMe` captures it before awaiting and
  // refuses to write a response that was already in flight when the cache was
  // cleared — otherwise a logout landing mid-request is undone by the
  // pre-logout 200 that arrives after it, resurrecting `status`. That race
  // predates the sticky flag but used to self-heal once the payload TTL
  // lapsed; with the flag it would persist until some later call happened to
  // 401, which nothing does while the cache is warm.
  private generation = 0;

  get(): Profile | null {
    if (Date.now() < this.expiresAt) {
      return this.data;
    }
    return null;
  }

  getStatus(): AuthStatus {
    return this.status;
  }

  getUserId(): string | null {
    return this.userId;
  }

  currentGeneration(): number {
    return this.generation;
  }

  set(profile: Profile): void {
    this.data = profile;
    this.expiresAt = Date.now() + TTL_MS;
    this.status = 'signed-in';
    this.userId = profile.userId;
  }

  clear(): void {
    this.data = null;
    this.expiresAt = 0;
    this.status = 'signed-out';
    this.userId = null;
    this.generation += 1;
  }

  // Drops only the payload's freshness window, leaving the sticky status (and
  // its userId) alone. BN_AUTH_CHANGE means some auth transition completed —
  // today always one this module already reflected via set()/clear() before
  // dispatching, but the event exists precisely so a future caller that
  // changes the session without going through fetchMe()/logout() (account
  // linking, a second tab) has somewhere to signal "the cached payload may
  // be stale now". Resetting the sticky status itself on every dispatch
  // would be wrong: a cache-hit fetchMe() call never re-touches status (it
  // returns before reaching requestMe()), so a reset here would leave
  // isLoggedIn() reporting 'unknown' for up to 5 more minutes after every
  // ordinary sign-in.
  invalidatePayload(): void {
    this.expiresAt = 0;
  }
}

export const profileCache = new ProfileCache();

if (typeof window !== 'undefined') {
  window.addEventListener(BN_AUTH_CHANGE, () => {
    profileCache.invalidatePayload();
  });
}

/**
 * The tri-state answer plus the account it speaks for, for callers that need
 * more than a boolean — the M7c history outbox stamps queued records with
 * `userId`, and can treat 'unknown' (cold start) differently from a
 * confirmed 'signed-out'.
 */
export function getAuthSnapshot(): AuthSnapshot {
  return { authState: profileCache.getStatus(), userId: profileCache.getUserId() };
}

/**
 * De-dupes concurrent callers. `ProfileCache.get()` only returns a payload once
 * a request has *resolved*, so every caller in the same tick misses the cache
 * and issues its own request — at boot that is each app's own resolve-sign-in
 * call plus `AuthWidget`'s plus `InviteBanner`'s, three `/auth/me` round trips
 * where one would do. Holding the in-flight promise makes the extra callers
 * free and makes `InviteBanner`'s "rides fetchMe's shared cache" comment true
 * for concurrent calls, not just sequential ones.
 */
let inFlight: Promise<Profile | null> | null = null;

/**
 * Discards anything cached or in flight because the session identity may have
 * just changed. Call before re-reading the profile after a sign-in.
 *
 * Without it the in-flight de-dupe above is wrong across exactly that moment:
 * a request issued *before* the new session existed is still pending, so the
 * post-sign-in `fetchMe()` joins it, receives its 401, and reports a
 * successful login as a failure. Bumping the generation additionally stops a
 * pre-sign-in 200 (the previous account's profile) from being written back
 * over the new one.
 */
export function resetForNewSession(): void {
  inFlight = null;
  profileCache.clear();
}

export async function fetchMe(): Promise<Profile | null> {
  const cached = profileCache.get();
  if (cached !== null) {
    return cached;
  }

  const existing = inFlight;
  if (existing !== null) {
    return existing;
  }

  const request = requestMe();
  inFlight = request;
  try {
    return await request;
  } finally {
    // Only release the slot if this request still owns it. Since
    // `resetForNewSession` can clear it mid-flight and a newer request take
    // its place, an unconditional reset would evict a still-pending successor
    // and send the next caller off to issue a third request of its own.
    if (inFlight === request) {
      inFlight = null;
    }
  }
}

async function requestMe(): Promise<Profile | null> {
  const generation = profileCache.currentGeneration();
  try {
    const response = await fetch(AUTH_URL + '/auth/me', {
      credentials: 'include',
    });

    if (response.status === 401) {
      // Authoritative "not signed in" — but only for the session this request
      // was issued under. `resetForNewSession` can leave an older request in
      // flight, and its 401 describes the session that was just replaced; the
      // generation check stops it clearing the newer one.
      if (profileCache.currentGeneration() === generation) {
        profileCache.clear();
      }
      return null;
    }

    const profile = (await response.json()) as Profile;
    // A logout (or another clear) landed while this was in flight: the answer
    // describes a session that no longer exists, so return it to this caller
    // but never write it back.
    if (profileCache.currentGeneration() !== generation) {
      return null;
    }
    profileCache.set(profile);
    return profile;
  } catch {
    return null;
  }
}

/**
 * Synchronous "is there a session" check. Deliberately NOT tied to the profile
 * cache's 5-minute freshness TTL — see `ProfileCache.status`. Returns false
 * for both 'unknown' and 'signed-out' — callers that must tell a cold start
 * apart from a confirmed sign-out (the M7c history policy) want
 * `getAuthSnapshot()` instead; callers that must not miss a signed-in player
 * on a cold start should await `fetchMe()`.
 */
export function isLoggedIn(): boolean {
  return profileCache.getStatus() === 'signed-in';
}

export async function logout(): Promise<void> {
  try {
    await fetch(AUTH_URL + '/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // best-effort
  }

  profileCache.clear();
  window.dispatchEvent(new CustomEvent(BN_AUTH_CHANGE));
}
